import { useState, useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import L from 'leaflet'
import { useATMStore } from '../store/atmStore'
import type { ATMDevice, ATMModel, ATMStatus, ATMZone } from '@atmvision/types'
import { modelIcon, modelInfocard } from '../modelImages'
import { useTheme } from '../hooks/useTheme'
import 'leaflet/dist/leaflet.css'

const STATUS_COLOR: Record<ATMStatus, string> = {
  IN_SERVICE: '#22c55e',
  OUT_OF_SERVICE: '#ef4444',
  WARNING: '#F89421',
  SUPERVISOR: '#02AFF0',
  LOADING: '#facc15',
  MAINTENANCE: '#8b5cf6',
  UNKNOWN: '#6b7280',
}

const STATUS_LABEL: Record<ATMStatus, string> = {
  IN_SERVICE: 'En Servicio',
  OUT_OF_SERVICE: 'Fuera de Servicio',
  WARNING: 'Advertencia',
  SUPERVISOR: 'Supervisor',
  LOADING: 'CIT en Ruta',
  MAINTENANCE: 'Mantenimiento',
  UNKNOWN: 'Desconocido',
}

// SVG inner content for each ATM status (24×24 viewBox, paths only — circle bg added by createATMIcon)
const STATUS_SVG_INNER: Record<ATMStatus, string> = {
  IN_SERVICE:     `<path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>`,
  OUT_OF_SERVICE: `<path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="white" stroke-width="2.2" stroke-linecap="round"/>`,
  WARNING:        `<path d="M12 8v5.5" stroke="white" stroke-width="2.2" stroke-linecap="round"/><circle cx="12" cy="17" r="1.4" fill="white"/>`,
  SUPERVISOR:     `<ellipse cx="12" cy="12" rx="5" ry="3.5" stroke="white" stroke-width="1.8" fill="none"/><circle cx="12" cy="12" r="2" fill="white"/>`,
  LOADING:        `<path d="M8.5 7.5a5 5 0 0 1 6 0" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/><path d="M8.5 16.5a5 5 0 0 0 6 0" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/><path d="M15.5 6l1 2.5-2.5 0.5" stroke="white" stroke-width="1.4" fill="none" stroke-linejoin="round"/>`,
  MAINTENANCE:    `<path d="M14.5 7.5a2.5 2.5 0 0 0-3.5 3.5l-3 3a1.8 1.8 0 0 0 2.5 2.5l3-3a2.5 2.5 0 0 0 3.5-3.5" stroke="white" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`,
  UNKNOWN:        `<path d="M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.5-2.5 2.5-2.5 4" stroke="white" stroke-width="2" stroke-linecap="round" fill="none"/><circle cx="12" cy="17" r="1.3" fill="white"/>`,
}

// Icon cache — 7 statuses × 2 highlight states = 14 icons max
const _iconCache = new Map<string, L.DivIcon>()

function getATMIcon(status: ATMStatus, highlighted: boolean): L.DivIcon {
  const key = `${status}-${highlighted}`
  const cached = _iconCache.get(key)
  if (cached) return cached

  const color = STATUS_COLOR[status]
  const size  = highlighted ? 32 : status === 'OUT_OF_SERVICE' ? 26 : 22
  const half  = size / 2
  const strokeWidth = highlighted ? 2.5 : 1.5
  const strokeColor = highlighted ? '#ffffff' : 'rgba(255,255,255,0.5)'
  const shadow = (status === 'OUT_OF_SERVICE' || highlighted)
    ? `filter: drop-shadow(0 0 4px ${color});`
    : ''

  const icon = L.divIcon({
    html: `<svg width="${size}" height="${size}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="${shadow}">
      <circle cx="12" cy="12" r="${highlighted ? 11.5 : 11}" fill="${color}" stroke="${strokeColor}" stroke-width="${strokeWidth}"/>
      ${STATUS_SVG_INNER[status]}
    </svg>`,
    className: '',
    iconSize:    [size, size],
    iconAnchor:  [half, half],
    popupAnchor: [0, -half - 2],
  })

  _iconCache.set(key, icon)
  return icon
}

const ZONES: Array<ATMZone | 'ALL'> = ['ALL', 'Distrito Nacional', 'Norte', 'Sur', 'Este']
const STATUSES: Array<ATMStatus | 'ALL'> = ['ALL', 'IN_SERVICE', 'OUT_OF_SERVICE', 'WARNING', 'SUPERVISOR']

export default function MapView() {
  const C = useTheme()
  const { fleet, events } = useATMStore()
  const [zoneFilter, setZoneFilter] = useState<ATMZone | 'ALL'>('ALL')
  const [statusFilter, setStatusFilter] = useState<ATMStatus | 'ALL'>('ALL')
  const [selected, setSelected] = useState<ATMDevice | null>(null)
  const [idSearch, setIdSearch] = useState('')

  const trimmedSearch = idSearch.trim().toUpperCase()

  const filtered = useMemo(() => {
    return fleet.filter((a) => {
      if (a.lat == null || a.lon == null || isNaN(a.lat) || isNaN(a.lon)) return false
      if (zoneFilter !== 'ALL' && a.zone !== zoneFilter) return false
      if (statusFilter !== 'ALL' && a.status !== statusFilter) return false
      if (trimmedSearch && !a.terminal_id.toUpperCase().includes(trimmedSearch)) return false
      return true
    })
  }, [fleet, zoneFilter, statusFilter, trimmedSearch])

  // Auto-select the single match when the ID search narrows to one ATM
  const exactMatch = trimmedSearch && filtered.length === 1 ? filtered[0] : null

  const counts = useMemo(() => {
    const acc: Record<string, number> = {}
    filtered.forEach((a) => { acc[a.status] = (acc[a.status] || 0) + 1 })
    return acc
  }, [filtered])

  // CIT dispatch ETA lookup: term_id → eta_minutes (for LOADING ATMs)
  const citETA = useMemo(() => {
    const map = new Map<string, { eta: number; teamId: string }>()
    for (const e of events) {
      if (e.raw_code !== 'CIT_DISPATCH_01') continue
      if (map.has(e.term_id)) continue  // keep the first (most recent dispatch)
      const meta = e.metadata as Record<string, unknown> | undefined
      if (typeof meta?.eta_minutes === 'number') {
        map.set(e.term_id, {
          eta:    meta.eta_minutes as number,
          teamId: (meta.team_id as string | undefined) ?? '',
        })
      }
    }
    return map
  }, [events])

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      {/* Sidebar */}
      <div style={{
        width: 240, background: C.card, borderRight: `1px solid ${C.border}`,
        display: 'flex', flexDirection: 'column', gap: 0, flexShrink: 0, overflow: 'auto',
      }}>
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 10 }}>
            Filtros de Mapa
          </div>

          {/* ── ID search ── */}
          <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
            Buscar por ID
          </label>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <input
              type="text"
              value={idSearch}
              onChange={(e) => {
                setIdSearch(e.target.value)
                setSelected(null)   // clear sidebar selection on new search
              }}
              placeholder="Ej. DRBR988"
              style={{
                width: '100%', background: C.card2, color: C.text,
                border: `1px solid ${trimmedSearch ? C.blue : C.border}`,
                borderRadius: 6, padding: '6px 28px 6px 8px',
                fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box',
                outline: 'none', transition: 'border-color 0.15s',
              }}
            />
            {idSearch && (
              <button
                onClick={() => { setIdSearch(''); setSelected(null) }}
                style={{
                  position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', border: 'none', color: C.muted, cursor: 'pointer',
                  fontSize: 14, lineHeight: 1, padding: 2,
                }}
                title="Limpiar"
              >×</button>
            )}
          </div>
          {trimmedSearch && (
            <div style={{ fontSize: 10, color: filtered.length === 0 ? C.red : filtered.length === 1 ? C.green : C.orange, marginBottom: 8 }}>
              {filtered.length === 0 ? '⚠ Sin coincidencias' : filtered.length === 1 ? `✓ ${filtered[0].terminal_id}` : `${filtered.length} coincidencias`}
            </div>
          )}

          <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
            Zona
          </label>
          <select
            value={zoneFilter}
            onChange={(e) => setZoneFilter(e.target.value as ATMZone | 'ALL')}
            style={{ width: '100%', background: C.card2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', marginBottom: 10 }}
          >
            {ZONES.map((z) => <option key={z} value={z}>{z === 'ALL' ? 'Todas las Zonas' : z}</option>)}
          </select>

          <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 4 }}>
            Estado
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ATMStatus | 'ALL')}
            style={{ width: '100%', background: C.card2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: 'inherit' }}
          >
            {STATUSES.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'Todos los Estados' : STATUS_LABEL[s as ATMStatus]}</option>)}
          </select>
        </div>

        {/* Count Summary */}
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            {filtered.length} ATMs mostrando
          </div>
          {Object.entries(counts).map(([status, count]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <svg width={14} height={14} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="11" fill={STATUS_COLOR[status as ATMStatus]} stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
                <g dangerouslySetInnerHTML={{ __html: STATUS_SVG_INNER[status as ATMStatus] }} />
              </svg>
              <span style={{ flex: 1, fontSize: 11, color: C.muted }}>{STATUS_LABEL[status as ATMStatus]}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLOR[status as ATMStatus] }}>{count}</span>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div style={{ padding: '12px 14px', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
            Leyenda
          </div>
          {(Object.keys(STATUS_COLOR) as ATMStatus[]).map((s) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <svg width={16} height={16} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <circle cx="12" cy="12" r="11" fill={STATUS_COLOR[s]} stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
                <g dangerouslySetInnerHTML={{ __html: STATUS_SVG_INNER[s] }} />
              </svg>
              <span style={{ fontSize: 11, color: C.muted }}>{STATUS_LABEL[s]}</span>
            </div>
          ))}
        </div>

        {/* Selected ATM Detail */}
        {(selected ?? exactMatch) && (
          <div style={{ padding: 0 }}>
            {(() => {
              const atm = selected ?? exactMatch!
              return (
                <>
                  {/* Infocard image */}
                  <div style={{ position: 'relative', background: '#0d1620', borderBottom: `1px solid ${C.border}` }}>
                    <img src={modelInfocard(atm.model as ATMModel)} alt={atm.model}
                      style={{ width: '100%', display: 'block', maxHeight: 180, objectFit: 'contain', padding: '10px 14px' }} />
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(transparent, rgba(10,18,28,0.9))', padding: '16px 12px 8px' }}>
                      <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: C.text }}>{atm.terminal_id}</div>
                      <div style={{ fontSize: 10, color: C.blue }}>{atm.model}</div>
                    </div>
                  </div>

                  <div style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: '3px 8px', fontSize: 11 }}>
                      <span style={{ color: C.muted }}>Zona</span>
                      <span style={{ color: C.text }}>{atm.zone}</span>
                      <span style={{ color: C.muted }}>Estado</span>
                      <span style={{ color: STATUS_COLOR[atm.status], fontWeight: 600 }}>{STATUS_LABEL[atm.status]}</span>
                      {atm.status === 'LOADING' && (() => {
                        const cit = citETA.get(atm.terminal_id)
                        return cit ? (<>
                          <span style={{ color: C.muted }}>CIT ETA</span>
                          <span style={{ color: '#ca8a04', fontWeight: 600 }}>
                            🚐 {cit.eta} min {cit.teamId ? `· ${cit.teamId}` : ''}
                          </span>
                        </>) : null
                      })()}
                      <span style={{ color: C.muted }}>Brigada</span>
                      <span style={{ color: C.text }}>{atm.brigada}</span>
                      <span style={{ color: C.muted }}>Localidad</span>
                      <span style={{ color: C.text }}>{atm.localidad}</span>
                      <span style={{ color: C.muted }}>Imagen SO</span>
                      <span style={{ color: atm.so_image ? C.text : C.muted, fontStyle: atm.so_image ? 'normal' : 'italic', fontSize: 10, wordBreak: 'break-all' }}>
                        {atm.so_image ?? 'Sin asignar'}
                      </span>
                    </div>

                    <div style={{ marginTop: 10 }}>
                      <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Cassettes</div>
                      {atm.cashCassettes.map((c) => {
                        const pct = c.current / c.capacity
                        const color = pct < 0.15 ? C.red : pct < 0.3 ? C.orange : C.green
                        return (
                          <div key={c.id} style={{ marginBottom: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.muted, marginBottom: 2 }}>
                              <span>{c.id} — RD${c.denomination}</span>
                              <span style={{ color }}>{c.current}/{c.capacity}</span>
                            </div>
                            <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct * 100}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )
            })()}
          </div>
        )}
      </div>

      {/* Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        <MapContainer
          center={[18.74, -70.16]}
          zoom={9}
          minZoom={6}
          maxZoom={19}
          style={{ width: '100%', height: '100%' }}
          zoomControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            maxZoom={19}
          />
          {filtered.map((atm) => {
            const isHighlighted = trimmedSearch !== '' && atm.terminal_id.toUpperCase().includes(trimmedSearch)
            return (
            <Marker
              key={atm.terminal_id}
              position={[atm.lat ?? 0, atm.lon ?? 0]}
              icon={getATMIcon(atm.status, isHighlighted)}
              eventHandlers={{ click: () => setSelected(atm) }}
            >
              <Popup>
                <div style={{ fontFamily: 'sans-serif', fontSize: 12, minWidth: 230 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <img src={modelIcon(atm.model as ATMModel)} alt="" style={{ height: 44, width: 'auto', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: C.primary }}>{atm.terminal_id}</div>
                      <div style={{ color: '#555', fontSize: 11 }}>{atm.model}</div>
                    </div>
                  </div>
                  <div style={{ color: '#444', marginBottom: 6, fontSize: 11 }}>{atm.address}</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: atm.status === 'LOADING' ? 6 : 0 }}>
                    <div style={{
                      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                      background: STATUS_COLOR[atm.status], color: '#fff', fontSize: 11, fontWeight: 600,
                    }}>
                      {STATUS_LABEL[atm.status]}
                    </div>
                    <span style={{ color: '#666', fontSize: 11 }}>{atm.brigada}</span>
                  </div>
                  {atm.status === 'LOADING' && (() => {
                    const cit = citETA.get(atm.terminal_id)
                    return cit ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        background: 'rgba(250,204,21,0.12)', border: '1px solid rgba(250,204,21,0.35)',
                        borderRadius: 6, padding: '4px 8px', fontSize: 11,
                      }}>
                        <span>🚐</span>
                        <span style={{ color: '#ca8a04', fontWeight: 600 }}>
                          CIT en ruta — ETA {cit.eta} min
                          {cit.teamId ? ` (${cit.teamId})` : ''}
                        </span>
                      </div>
                    ) : (
                      <div style={{ fontSize: 11, color: '#ca8a04' }}>🚐 CIT en ruta</div>
                    )
                  })()}
                </div>
              </Popup>
            </Marker>
          )})}

        </MapContainer>
      </div>
    </div>
  )
}
